import React, { useState } from "react";
import { Grid, Typography, Paper, Divider, Stack, IconButton, Avatar, Box, Button } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { login } from '../services/api';

// Import background video from images folder
import loginBgVideo from '../images/Login background loop.mp4';

// Import social logos (PNG files)
import appleLogo from '../images/apple.png';
import googleLogo from '../images/google.png';
import outlookLogo from '../images/outlook.png';
import yahooLogo from '../images/yahoo.png';

const socialProviders = [
  { name: 'Google', logo: googleLogo },
  { name: 'Apple', logo: appleLogo },
  { name: 'Yahoo', logo: yahooLogo },
  { name: 'Outlook', logo: outlookLogo },
];

const Login = ({ adminMode = false }) => {
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    setLoginData({
      ...loginData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    
    if (adminMode) {
      // For admin login, check against the credentials
      if (loginData.email === "info@lantenapm.com" && loginData.password === "admin") {
        // Store authentication data
        localStorage.setItem('isAdminAuthenticated', 'true');
        localStorage.setItem('token', 'admin-token');
        localStorage.setItem('user', JSON.stringify({
          id: '1',
          email: 'info@lantenapm.com',
          firstName: 'Admin',
          lastName: 'User',
          role: 'admin'
        }));
        
        // Redirect to admin dashboard
        const currentDomain = window.location.hostname;
        
        // If already on admin domain, just go to dashboard
        if (currentDomain.startsWith('admin.')) {
          navigate("/");
        } else {
          // Otherwise, redirect to admin domain
          const adminUrl = process.env.REACT_APP_ADMIN_URL || 'https://admin.lantenapm.com';
          window.location.href = adminUrl;
        }
      } else {
        setErrorMessage("Invalid administrator credentials.");
      }
      return;
    }
    
    // Regular user login
    try {
      const response = await login(loginData);
      const { token, user } = response.data;
      
      // Store authentication data
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // Redirect based on user role
      if (user.role === "admin") {
        // Redirect admin users to admin domain
        const adminUrl = process.env.REACT_APP_ADMIN_URL || 'https://admin.lantenapm.com';
        window.location.href = adminUrl;
      } else if (user.role === "manager") {
        navigate("/manager");
      } else {
        navigate("/user");
      }
    } catch (error) {
      console.error(error.response?.data || error.message);
      if (error.response && error.response.data && error.response.data.error) {
        setErrorMessage(error.response.data.error);
      } else {
        setErrorMessage("Server error. Please try again later.");
      }
    }
  };

  const handleSocialLogin = (provider) => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8787';
    let url = "";
    switch (provider) {
      case 'Google':
        url = `${apiUrl}/api/auth/google`;
        break;
      case 'Apple':
        url = `${apiUrl}/api/auth/apple`;
        break;
      case 'Yahoo':
        url = `${apiUrl}/api/auth/yahoo`;
        break;
      case 'Outlook':
        url = `${apiUrl}/api/auth/outlook`;
        break;
      default:
        return;
    }
    window.location.href = url;
  };

  // Glowing style for sign up button if error indicates invalid credentials
  const signUpButtonStyle = errorMessage && errorMessage.toLowerCase().includes("invalid credentials")
    ? { animation: 'glow 1s ease-in-out infinite alternate' }
    : {};

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Background Video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: -1,
        }}
      >
        <source src={loginBgVideo} type="video/mp4" />
      </video>

      {/* Content */}
      <Grid container justifyContent="center" alignItems="center" style={{ minHeight: '100vh', padding: '20px' }}>
        <Grid item xs={12} sm={8} md={6} lg={4}>
          <Paper elevation={5} sx={{ p: 4, borderRadius: 2, backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              {adminMode ? (
                <AdminPanelSettingsIcon sx={{ fontSize: 48, color: '#0a233b', mb: 1 }} />
              ) : null}
              <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#0a233b' }}>
                {adminMode ? 'Administrator Login' : 'Welcome Back'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6a6565', mt: 1 }}>
                {adminMode ? 
                  'Secure access for Lantena Property Managers administrators only' : 
                  'Sign in to continue to your account'}
              </Typography>
            </Box>

            {errorMessage && (
              <Typography color="error" sx={{ mb: 2, textAlign: 'center' }}>
                {errorMessage} {!adminMode && "Please sign up if you don't have an account."}
              </Typography>
            )}

            <form onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <input
                  type={adminMode ? "text" : "email"}
                  name="email"
                  placeholder={adminMode ? "Username" : "Email Address"}
                  value={loginData.email}
                  onChange={handleChange}
                  required
                  style={{
                    padding: '12px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontSize: '16px'
                  }}
                />
                <input
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={loginData.password}
                  onChange={handleChange}
                  required
                  style={{
                    padding: '12px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontSize: '16px'
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    backgroundColor: '#f66b10',
                    '&:hover': { backgroundColor: '#e05a00' },
                    py: 1.5,
                    fontWeight: 'bold',
                  }}
                >
                  {adminMode ? 'Login to Dashboard' : 'Login'}
                </Button>
              </Stack>
            </form>

            {!adminMode && (
              <>
                <Divider sx={{ my: 3 }}>OR</Divider>

                {/* Social Login Options */}
                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
                  {socialProviders.map((provider) => (
                    <IconButton
                      key={provider.name}
                      onClick={() => handleSocialLogin(provider.name)}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: '50%',
                        p: 1,
                        '&:hover': { backgroundColor: '#f5f5f5' }
                      }}
                    >
                      <Avatar src={provider.logo} alt={provider.name} sx={{ width: 24, height: 24 }} />
                    </IconButton>
                  ))}
                </Stack>

                {/* Sign Up Section */}
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Don't have an account?</Typography>
                  <Button variant="contained" component={Link} to="/contact" sx={{ backgroundColor: '#2C3E50', color: 'white', ...signUpButtonStyle }}>
                    Contact Us
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Login;
